import {
  Alert, Button, Card, Descriptions, Empty, Image, Input, Modal, Space, Spin, Typography,
} from 'antd';
import rentalApi from 'api/rentalApi';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  formatDate, formatDateTime, GENDER_LABELS, getRentalErrorMessage,
  IDENTITY_DOCUMENT_TYPE_LABELS, LOCATION_TYPE_LABELS, PARTICIPANT_TYPE_LABELS, PartnerStatusTag,
} from '../../rentalConstants';

const { Text, Title } = Typography;

function RentalPartnerDetailScreen() {
  const router = useRouter();
  const rentalPartnerId = router.query?.rentalPartnerId;
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = String(user?.role || '').toUpperCase() === 'ADMIN';
  const canReview = isAdmin || permissionList?.some((permission) => permission?.code === 'RENTAL_PARTNER_REVIEW');

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!rentalPartnerId) return;
    setLoading(true);
    try {
      const response = await rentalApi.getPartner(rentalPartnerId);
      setDetail(response?.partner || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [rentalPartnerId]);

  useEffect(() => { load(); }, [load]);

  const approve = () => Modal.confirm({
    title: 'Duyệt hồ sơ đối tác?',
    content: 'Đối tác sẽ có thể thêm xe để bắt đầu đặt cọc sau khi duyệt.',
    okText: 'Duyệt hồ sơ',
    cancelText: 'Huỷ',
    onOk: async () => {
      setActionLoading(true);
      try {
        const response = await rentalApi.approvePartner(rentalPartnerId);
        setDetail(response?.partner || null);
        ToastHelper.showSuccess('Đã duyệt hồ sơ đối tác');
      } catch (error) {
        ToastHelper.showError(getRentalErrorMessage(error));
      } finally {
        setActionLoading(false);
      }
    },
  });

  const submitReject = async () => {
    if (!reason.trim()) return;
    setActionLoading(true);
    try {
      const response = await rentalApi.rejectPartner(rentalPartnerId, reason.trim());
      setDetail(response?.partner || null);
      setRejectOpen(false);
      setReason('');
      ToastHelper.showSuccess('Đã từ chối hồ sơ đối tác');
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      <div>
        {UIHelper.buildBreadcrumb([
          { title: 'Đối tác cho thuê xe', onPress: () => router.navigate(-1) },
          { title: `Hồ sơ #${rentalPartnerId}` },
        ])}
      </div>
      <div className="container-fluid px-4 my-4">
        <Card>
          <Spin spinning={loading}>
            {!detail && !loading ? <Empty description="Không tải được hồ sơ" /> : detail && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <Title level={4} className="mb-0">
                    Hồ sơ #{detail.rentalPartnerId}
                  </Title>
                  <PartnerStatusTag status={detail.status} />
                </div>

                {detail.duplicateWarning && (
                  <Alert
                    type="error"
                    showIcon
                    message="Phát hiện số CCCD/giấy tờ trùng với hồ sơ đối tác khác"
                    description="Cần đối chiếu ảnh giấy tờ trước khi duyệt."
                  />
                )}

                <Descriptions title="Thông tin cá nhân" bordered column={1} size="small">
                  <Descriptions.Item label="Tài khoản">{detail.user?.fullname || '—'} ({detail.user?.phone || detail.user?.email || `User #${detail.userId}`})</Descriptions.Item>
                  <Descriptions.Item label="Người liên hệ">{detail.contactName || '—'}</Descriptions.Item>
                  <Descriptions.Item label="SĐT liên hệ">{detail.contactPhone || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Email liên hệ">{detail.contactEmail || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Ngày sinh">{formatDate(detail.dateOfBirth)}</Descriptions.Item>
                  <Descriptions.Item label="Giới tính">{GENDER_LABELS[detail.gender] || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Loại đối tác">{PARTICIPANT_TYPE_LABELS[detail.participantType] || '—'}</Descriptions.Item>
                </Descriptions>

                <Descriptions title="Giấy tờ tuỳ thân" bordered column={1} size="small">
                  <Descriptions.Item label="Loại giấy tờ">{IDENTITY_DOCUMENT_TYPE_LABELS[detail.identityDocumentType] || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Số giấy tờ">{detail.identityNumber || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Ảnh giấy tờ">
                    <Space>
                      {detail.identityFrontPhoto && (
                        <Image width={120} src={Utils.getFullUrl(detail.identityFrontPhoto.relativePath)} alt="Mặt trước" />
                      )}
                      {detail.identityBackPhoto && (
                        <Image width={120} src={Utils.getFullUrl(detail.identityBackPhoto.relativePath)} alt="Mặt sau" />
                      )}
                      {!detail.identityFrontPhoto && !detail.identityBackPhoto && '—'}
                    </Space>
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions title="Địa điểm giao nhận xe" bordered column={1} size="small">
                  <Descriptions.Item label="Tên địa điểm">{detail.locationName || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Loại địa điểm">{LOCATION_TYPE_LABELS[detail.locationType] || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Địa chỉ chi tiết">{detail.handoverAddress || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Toạ độ">
                    {detail.handoverLatitude != null ? `${detail.handoverLatitude}, ${detail.handoverLongitude}` : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ảnh địa điểm">
                    {(detail.locationPhotos || []).length ? (
                      <Space wrap>
                        {detail.locationPhotos.map((photo) => (
                          <Image key={photo.mediaId} width={120} src={Utils.getFullUrl(photo.relativePath)} />
                        ))}
                      </Space>
                    ) : '—'}
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions title="Thông tin ngân hàng" bordered column={1} size="small">
                  <Descriptions.Item label="Ngân hàng">{detail.bankCode || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Số tài khoản">{detail.bankAccountNumber || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Chủ tài khoản">{detail.bankAccountHolder || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Ngày tạo">{formatDateTime(detail.createdAt)}</Descriptions.Item>
                </Descriptions>

                {detail.reviewReason && (
                  <Alert type="warning" showIcon message="Lý do xử lý gần nhất" description={detail.reviewReason} />
                )}
                {detail.reviewer && (
                  <Text type="secondary">
                    Duyệt bởi {detail.reviewer.fullname} lúc {formatDateTime(detail.reviewedAt)}
                  </Text>
                )}

                {detail.status === 'PENDING' && (
                  canReview ? (
                    <Space wrap>
                      <Button type="primary" onClick={approve} loading={actionLoading}>Duyệt hồ sơ</Button>
                      <Button danger onClick={() => setRejectOpen(true)} loading={actionLoading}>Từ chối</Button>
                    </Space>
                  ) : <Alert type="info" showIcon message="Tài khoản chỉ có quyền xem, không có RENTAL_PARTNER_REVIEW." />
                )}
              </Space>
            )}
          </Spin>
        </Card>
      </div>

      <Modal
        title="Từ chối hồ sơ đối tác"
        open={rejectOpen}
        okText="Xác nhận từ chối"
        cancelText="Huỷ"
        confirmLoading={actionLoading}
        okButtonProps={{ danger: true, disabled: !reason.trim() }}
        onCancel={() => { setRejectOpen(false); setReason(''); }}
        onOk={submitReject}
      >
        <Input.TextArea
          rows={4}
          maxLength={1000}
          showCount
          value={reason}
          placeholder="Nêu rõ lý do từ chối để đối tác biết cần bổ sung gì"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </div>
  );
}

export default RentalPartnerDetailScreen;
